import React from 'react';
import PropTypes from 'prop-types';
import Accordion from '../../../Accordion';
import NumberInput from '../components/NumberInput';
import NumberInputPercentage from '../components/NumberInputPercentage';
import useAdvancedSettings from './useAdvancedSettings';

const CircularProgressBar = ({ percentage, children }) => {
    const radius = 100;
    const strokeWidth = 8;
    const normalizedRadius = radius - strokeWidth * 2;
    const circumference = normalizedRadius * 2 * Math.PI;
    const strokeDasharray = `${circumference} ${circumference}`;
    const strokeDashoffset = circumference - (percentage / 100) * circumference;

    const getStrokeColor = () => {
        if (percentage < 100) return '#C14B0F'; // Red for incomplete
        if (percentage === 100) return '#277D32'; // Green for complete
        return '#C14B0F'; // Red for over 100%
    };

    return (
        <div className="circular-progress-bar" style={{ position: 'relative', display: 'inline-block' }}>
            <svg
                height={radius * 2}
                width={radius * 2}
                style={{ transform: 'rotate(-90deg)' }}
            >
                <circle
                    stroke="#e9ecef"
                    fill="transparent"
                    strokeWidth={strokeWidth}
                    r={normalizedRadius}
                    cx={radius}
                    cy={radius}
                />
                <circle
                    stroke={getStrokeColor()}
                    fill="transparent"
                    strokeWidth={strokeWidth}
                    strokeDasharray={strokeDasharray}
                    strokeDashoffset={strokeDashoffset}
                    strokeLinecap="round"
                    r={normalizedRadius}
                    cx={radius}
                    cy={radius}
                    style={{
                        transition: 'stroke-dashoffset 0.3s ease-in-out, stroke 0.3s ease-in-out'
                    }}
                />
            </svg>
            <div
                style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    color: getStrokeColor()
                }}
            >
                {children && <div>{children}</div>}
            </div>
        </div>
    );
};

CircularProgressBar.propTypes = {
    percentage: PropTypes.number.isRequired
};

const AccordionInner = (props) => {
    return (
        <Accordion level={2} isPercentageDivision={props.isPercentageDivision} label={props.label}>
            <div className='calculator-result__dialog-additional-settings__energy-cost'>
                {props.children}
            </div>
        </Accordion>
    );
};

AccordionInner.props = {
    label: PropTypes.string,
    isPercentageDivision: PropTypes.bool,
    children: PropTypes.element
};

const AdvancedSettings = ({ props }) => {

    const [energyDivisionTotalLabel, setEnergyDivisionTotalLabel] = React.useState('');

    const { calculationContext, percentageDivisionFields, costFields, productionEfficiencyFields, ghgFields } = useAdvancedSettings(props.translations);

    const distributeRemainingPercent = () => {
        if (calculationContext.totalDivisionPercent >= 100) {
            return;
        }
        const remainingPercent = 100 - calculationContext.totalDivisionPercent;
        const unlockedFields = percentageDivisionFields.filter(field => !field.locked);
        const percentPerField = Math.floor(remainingPercent / unlockedFields.length);
        const remainder = remainingPercent % unlockedFields.length;

        unlockedFields.forEach((field, index) => {
            const additionalPercent = percentPerField + (index < remainder ? 1 : 0);
            field.onChange(Number(field.value) + additionalPercent);
        });
    };

    const balanceEvenly = () => {
        const lockedValue = percentageDivisionFields.filter(field => field.locked).reduce((sum, field) => sum + Number(field.value), 0);
        const remainingPercent = 100 - lockedValue;
        if (remainingPercent < 0) {
            return;
        }

        const unlockedFields = percentageDivisionFields.filter(field => !field.locked);
        const percentPerField = Math.floor(remainingPercent / unlockedFields.length);
        const remainder = remainingPercent % unlockedFields.length;

        unlockedFields.forEach((field, index) => {
            const newValue = percentPerField + (index < remainder ? 1 : 0);
            field.onChange(newValue);
        });
    };

    React.useEffect(() => {
        if (calculationContext.totalDivisionPercent < 100) {
            setEnergyDivisionTotalLabel(`${100 - calculationContext.totalDivisionPercent}% ${props.translations.energyDivisionTotalIncreaseByLabel}`);
        } else if (calculationContext.totalDivisionPercent > 100) {
            setEnergyDivisionTotalLabel(`${calculationContext.totalDivisionPercent - 100}% ${props.translations.energyDivisionTotalTooDecreaseByLabel}`);
        } else {
            setEnergyDivisionTotalLabel(props.translations.energyDivisionTotalCorrectLabel);
        }

    }, [calculationContext.heatingDivisionPercentage, calculationContext.coolingDivisionPercentage, calculationContext.domesticHotWaterDivisionPercentage, calculationContext.buildingElectricityDivisionPercentage, calculationContext.businessElectricityDivisionPercentage]);

    return (
        <Accordion level={1} label={props.translations.advancedSettings}>
            <AccordionInner label={props.translations.energyCostLabel} >
                {costFields.map((field, index) => (
                    <NumberInput
                        key={index}
                        label={field.label}
                        assistiveText={field.assistiveText}
                        onChange={field.onChange}
                        value={field.value}
                        suffix={field.suffix}
                        center
                    />
                ))}
            </AccordionInner>

            <AccordionInner label={props.translations.energyEfficiencyFactorLabel} >
                {productionEfficiencyFields.map((field, index) => (
                    <NumberInput
                        key={index}
                        label={field.label}
                        assistiveText={field.assistiveText}
                        onChange={field.onChange}
                        value={field.value}
                        suffix={field.suffix}
                        center
                    />
                ))}
            </AccordionInner>

            <AccordionInner label={props.translations.greenHouseGasEquivalentLabel} >
                {ghgFields.map((field, index) => (
                    <NumberInput
                        key={index}
                        label={field.label}
                        assistiveText={field.assistiveText}
                        onChange={field.onChange}
                        value={field.value}
                        suffix={field.suffix}
                        center
                    />
                ))}
            </AccordionInner>

            <AccordionInner isPercentageDivision label={props.translations.energyDivisionLabel} >
                <div className='energy-division-total' >
                    <CircularProgressBar percentage={calculationContext.totalDivisionPercent}>
                        <div>
                            <div className={`total-division-percent ${calculationContext.invalidDivisionPercent ? 'invalid' : ''}`}>{calculationContext.totalDivisionPercent}%</div>
                        </div>
                    </CircularProgressBar>
                    <p className={`energy-division-total-label ${calculationContext.invalidDivisionPercent ? 'invalid' : ''}`}>{energyDivisionTotalLabel}</p>
                </div>
                <div className='energy-division-inputs' >

                    {percentageDivisionFields.map((field, index) => (
                        <NumberInputPercentage
                            key={index}
                            label={field.label}
                            assistiveText={field.assistiveText}
                            onChange={field.onChange}
                            value={field.value}
                            suffix="%"
                            center
                            invalid={field.invalid}
                            locked={field.locked}
                            setLocked={field.setLocked}
                        />
                    ))}
                </div>
                {calculationContext.totalDivisionPercent < 100 &&
                    <div>
                        <div className='energy-division-buttons'>
                            {calculationContext.totalDivisionPercent > 0 &&
                                <button aria-describedby='locked-fields-info' onClick={distributeRemainingPercent} className='button'>{props.translations.distributeRemainingLabel}</button>
                            }
                            <button aria-describedby='locked-fields-info' onClick={balanceEvenly} className='button button--ghost'>{props.translations.balanceEvenlyLabel}</button>
                        </div>
                        <p id='locked-fields-info'>{props.translations.lockedFieldsInfoLabel}</p>
                    </div>
                }
                {props.translations.energyDivisionTips &&
                    <div className="energy-division-tips" dangerouslySetInnerHTML={{ __html: props.translations.energyDivisionTips }}></div>
                }

            </AccordionInner>

        </Accordion >
    );
};

AdvancedSettings.props = {
    translations: PropTypes.any
};

export default AdvancedSettings;
